"use client";

import { useOptimizedSession } from "../hooks/use-optimized-session";
import { useTranslations } from "next-intl";
import { useMemo } from "react";
import { FlipWords } from "ui/flip-words";
import type { Agent } from "app-types/agent";

function getGreetingByTime() {
  const hour = new Date().getHours();
  if (hour < 12) return "goodMorning";
  if (hour < 18) return "goodAfternoon";
  return "goodEvening";
}

interface ChatGreetingProps {
  agent?: Agent;
}

export const ChatGreeting = ({ agent }: ChatGreetingProps) => {
  const { data: session } = useOptimizedSession();

  const t = useTranslations("Chat.Greeting");

  const user = session?.user;

  const word = useMemo(() => {
    if (agent) {
      // Agent-specific greeting
      const agentWords = [
        `Hello! I'm ${agent.name}, your specialized assistant.`,
        `Hi there! ${agent.name} here, ready to help.`,
        `Welcome! I'm ${agent.name}. How can I assist you today?`,
        `Greetings! ${agent.name} at your service.`,
      ];
      return agentWords[Math.floor(Math.random() * agentWords.length)];
    }

    if (!user?.name) return "";
    const words = [
      t(getGreetingByTime(), { name: user.name }),
      t("niceToSeeYouAgain", { name: user.name }),
      t("whatAreYouWorkingOnToday", { name: user.name }),
      t("letMeKnowWhenYoureReadyToBegin"),
      t("whatAreYourThoughtsToday"),
      t("whereWouldYouLikeToStart"),
      t("whatAreYouThinking", { name: user.name }),
    ];
    return words[Math.floor(Math.random() * words.length)];
  }, [user?.name, agent, t]);

  return (
    <div
      key="welcome"
      className="max-w-3xl mx-auto my-4 min-h-20 animate-in fade-in-0 duration-500 delay-300"
    >
      <div className="rounded-xl p-6 flex flex-col gap-2 leading-relaxed text-center">
        {agent && (
          <div className="flex items-center justify-center gap-2 mb-2">
            {agent.icon && (
              <span className="text-2xl" style={agent.icon.style}>
                {agent.icon.value}
              </span>
            )}
            <span className="text-sm text-muted-foreground">
              Agent: {agent.name}
            </span>
          </div>
        )}
        <h1 className="text-2xl md:text-3xl">
          {word ? <FlipWords words={[word]} className="text-primary" /> : ""}
        </h1>
        {agent?.description && (
          <p className="text-sm text-muted-foreground mt-2">
            {agent.description}
          </p>
        )}
      </div>
    </div>
  );
};
